import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { UserRole } from '@rutatec/shared-types'
import { getUserDisplayName } from '@rutatec/shared-utils'

interface AuthUser {
  id: string
  email: string
  role: UserRole
  empresaId: string | null
  conductorId: string | null
  displayName: string | null
  nomeCompleto: string
  rating?: number
}

interface AuthState {
  accessToken: string | null
  // refreshToken removido do estado — agora em cookie httpOnly (segurança XSS)
  user: AuthUser | null
  isAuthenticated: boolean

  setTokens: (accessToken: string) => void
  setUser: (user: AuthUser) => void
  updateDisplayName: (displayName: string) => void
  logout: () => void

  /** Nome de exibição derivado: displayName ?? nome local do email */
  getDisplayName: () => string
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,

      setTokens: (accessToken) =>
        set({ accessToken, isAuthenticated: true }),

      setUser: (user) => set({ user }),

      updateDisplayName: (displayName) =>
        set((state) => ({
          user: state.user ? { ...state.user, displayName } : null,
        })),

      logout: () =>
        set({
          accessToken: null,
          user: null,
          isAuthenticated: false,
        }),

      getDisplayName: () => {
        const { user } = get()
        if (!user) return ''
        return getUserDisplayName(user.displayName, user.email)
      },
    }),
    {
      name: 'rutatec-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        accessToken:     state.accessToken,
        user:            state.user,
        isAuthenticated: state.isAuthenticated,
        // refreshToken NÃO persiste — está em cookie httpOnly
      }),
    }
  )
)

/** Hook para redirecionar ao portal correto por role */
export const getPortalPath = (role: UserRole): string => {
  if (['REMITENTE_ADMIN', 'REMITENTE_OPERATOR'].includes(role)) return '/r/dashboard'
  if (['CONDUCTOR_AUTONOMO', 'CONDUCTOR_VINCULADO'].includes(role)) return '/c/panel'
  if (['TRANSPORTADORA_ADMIN', 'TRANSPORTADORA_OPERATOR'].includes(role)) return '/t/dashboard'
  if (['RUTATEC_ADMIN', 'RUTATEC_OPS'].includes(role)) return '/admin/dashboard'
  return '/login'
}
